import { Link, useParams } from "react-router-dom";
import SeriesCard from "@/components/base/SeriesCard";
import SectionHeading from "@/components/base/SectionHeading";
import { useShare } from "@/hooks/useShare";
import { seriesList } from "@/mocks/series";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const { share, status } = useShare();

  const series = seriesList.find((item) => item.id === id);

  if (!series) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-film-line text-4xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          该短剧不存在或已被删除
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-500">
          它可能已经下架，或者链接地址有误。你可以返回分类页继续浏览其他 AI 短剧作品。
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link
            to="/category"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="whitespace-nowrap">返回短剧分类</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-home-4-line"></i>
            <span className="whitespace-nowrap">回到首页</span>
          </Link>
        </div>
      </div>
    );
  }

  const related = seriesList
    .filter((item) => item.id !== series.id)
    .map((item) => {
      let weight = 0;
      if (item.categoryId === series.categoryId) weight += 3;
      weight += item.tags.filter((tag) => series.tags.includes(tag)).length;
      return { item, weight };
    })
    .sort((a, b) => b.weight - a.weight || b.item.score - a.item.score)
    .slice(0, 4)
    .map((entry) => entry.item);

  const metaItems = [
    { label: "类型", value: series.categoryName, icon: "ri-price-tag-3-line" },
    { label: "上线日期", value: series.releaseDate, icon: "ri-calendar-line" },
    { label: "集数", value: `共 ${series.episodes} 集`, icon: "ri-stack-line" },
    { label: "单集时长", value: series.duration, icon: "ri-timer-line" },
    { label: "出品", value: series.director, icon: "ri-user-star-line" },
    { label: "热度", value: series.popularity.toLocaleString("en-US"), icon: "ri-fire-line" },
  ];

  const handleShare = () => {
    share(series.title, series.description, window.location.href);
  };

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={series.banner}
            alt={`${series.title} 剧照`}
            title={`${series.title} · ai短剧详情`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/50"></div>
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-10">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-secondary-200" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category" className="transition-colors hover:text-accent-400">
              短剧分类
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category?type=${series.categoryId}`}
              className="transition-colors hover:text-accent-400"
            >
              {series.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-medium text-background-50">{series.title}</span>
          </nav>

          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-4">
              <div className="aspect-[3/4] w-40 overflow-hidden rounded-lg border border-background-50/20 md:w-52 lg:w-full lg:max-w-xs">
                <img
                  src={series.cover}
                  alt={`${series.title} 封面`}
                  title={`${series.title} · ai短剧`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 font-semibold text-foreground-950">
                  {series.categoryName}
                </span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1 font-medium text-background-50">
                  {series.year}
                </span>
                <span className="flex items-center gap-1 font-medium text-secondary-100">
                  <i className="ri-star-fill text-accent-400"></i>
                  {series.score} 分
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {series.title}
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-secondary-100 md:text-base">
                {series.description}
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {series.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-background-50/10 px-3 py-1.5 text-xs font-medium text-secondary-100 transition-colors hover:bg-accent-500 hover:text-foreground-950"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-share-forward-line text-lg"></i>
                  <span className="whitespace-nowrap">分享这部短剧</span>
                </button>
                <Link
                  to={`/category?type=${series.categoryId}`}
                  className="inline-flex items-center gap-2 rounded-md border border-background-50/30 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/15"
                >
                  <i className="ri-arrow-left-line"></i>
                  <span className="whitespace-nowrap">返回分类页</span>
                </Link>
                {status === "copied" && (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-background-50/15 px-3 py-2 text-xs text-background-50">
                    <i className="ri-check-line text-accent-400"></i>
                    链接已复制到剪贴板
                  </span>
                )}
                {status === "failed" && (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-background-50/15 px-3 py-2 text-xs text-background-50">
                    <i className="ri-error-warning-line text-accent-400"></i>
                    当前浏览器暂不支持分享，请手动复制地址
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-book-open-line text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base md:leading-loose">
                {series.description}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base md:leading-loose">
                作为一部由人工智能深度参与创作的短剧，《{series.title}》在分镜、美术与声音设计环节
                均引入了生成式工具，同时保留人工打磨剧本与表演节奏的环节。它试图证明：当技术足够成熟，
                真正决定一部作品好坏的，依然是故事本身。
              </p>

              <div className="mt-8 rounded-lg bg-background-100 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-information-line"></i>
                  </span>
                  <p className="text-xs leading-relaxed text-foreground-500 md:text-sm">
                    本站为内容推荐与资讯平台，不提供视频播放功能。如需观看，请在正版内容平台搜索对应作品，
                    本站信息仅供参考与交流。
                  </p>
                </div>
              </div>
            </section>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-group-line text-primary-500"></i>
                主创与出演
              </h2>
              <div className="mt-5 flex flex-wrap gap-3">
                {series.cast.map((person) => (
                  <span
                    key={person}
                    className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 text-sm text-foreground-700"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-[11px] font-semibold text-background-50">
                      {person.slice(0, 1)}
                    </span>
                    {person}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-xs text-foreground-400">
                {series.director} · 以上人物与团队均为虚构示例，用于展示详情页信息结构。
              </p>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">基本信息</h2>
                <dl className="mt-5 space-y-4">
                  {metaItems.map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={`${item.icon} text-base`}></i>
                      </span>
                      <div className="min-w-0">
                        <dt className="text-xs text-foreground-400">{item.label}</dt>
                        <dd className="text-sm font-medium text-foreground-800">{item.value}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">评分与热度</h2>
                <div className="mt-4 flex items-end gap-3">
                  <span className="font-heading text-4xl font-bold text-accent-600">
                    {series.score.toFixed(1)}
                  </span>
                  <div className="pb-1.5">
                    <div className="flex text-accent-500">
                      {[0, 1, 2, 3, 4].map((index) => (
                        <i
                          key={index}
                          className={
                            index < Math.round(series.score / 2)
                              ? "ri-star-fill text-base"
                              : "ri-star-line text-base text-background-500"
                          }
                        ></i>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-foreground-400">综合站内热度与观众反馈</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleShare}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-link"></i>
                  <span className="whitespace-nowrap">复制分享链接</span>
                </button>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <SectionHeading
            eyebrow="Recommended"
            title="相关短剧推荐"
            description={`根据《${series.title}》的分类与标签相似度为你挑选。`}
            to={`/category?type=${series.categoryId}`}
            toLabel="查看更多同类"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-5">
            {related.map((item) => (
              <SeriesCard key={item.id} series={item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}